import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FeedbackRating } from '@rbx/conv-ai-provider';
import ShareRatedFeedbackDialog from '../components/ShareRatedFeedbackDialog';
import ShareGeneralFeedbackDialog from '../components/ShareGeneralFeedbackDialog';
import useLoadedFeedbackConfig from '../hooks/useLoadedFeedbackConfig';

type onSubmitOptions = {
  isPublic: boolean;
};

export type SubmitFeedbackCallback = (
  ratingValue: FeedbackRating,
  comment?: string,
  options?: onSubmitOptions,
) => void;

export const ShareFeedbackContext = createContext<
  | ({
      openFeedbackDialog: (
        rating?: FeedbackRating,
        submitFeedbackFn?: SubmitFeedbackCallback,
      ) => void;
    } & ReturnType<typeof useLoadedFeedbackConfig>)
  | null
>(null);

export const enum FeedbackDialogType {
  RatedAssistantFeedback = 'RatedAssistantFeedback',
  GeneralDocsFeedback = 'GeneralDocsFeedback',
}

type RatedFeedbackDialogType = FeedbackDialogType.RatedAssistantFeedback;

const isRatedFeedback = (
  fdbkDialogType: FeedbackDialogType,
): fdbkDialogType is RatedFeedbackDialogType =>
  fdbkDialogType === FeedbackDialogType.RatedAssistantFeedback;

interface ShareFeedbackProviderProps {
  feedbackDialogType: FeedbackDialogType;
  children?: React.ReactNode | React.ReactChildren;
}

export const ShareFeedbackProvider: React.FC<
  React.PropsWithChildren<ShareFeedbackProviderProps>
> = ({ feedbackDialogType, children }) => {
  const isRatedFeedbackForm = isRatedFeedback(feedbackDialogType);
  const { hasFeedbackServiceError, ...feedbackAllowChecks } = useLoadedFeedbackConfig();
  const [feedbackDialogRating, setFeedbackDialogRating] = useState<FeedbackRating>();
  const [submitFeedback, setSubmitFeedback] = useState<{ callback: SubmitFeedbackCallback }>();
  const [open, setOpen] = useState(false);
  const openFeedbackDialog = useCallback(
    (rating?: FeedbackRating, submitFeedbackCb?: SubmitFeedbackCallback) => {
      if (isRatedFeedbackForm && !rating) throw new Error(`Rating expected on rated feedback form`);
      if (!submitFeedbackCb) throw new Error(`submitFeedback callback required`);
      setFeedbackDialogRating(rating);
      setSubmitFeedback({ callback: submitFeedbackCb });
      if (!hasFeedbackServiceError) setOpen(true);
      // expose hasFeedbackServiceError in context for handling downstream
    },
    [hasFeedbackServiceError, isRatedFeedbackForm],
  );
  const onClose = () => setOpen(false);
  const contextValue = useMemo(() => {
    return {
      openFeedbackDialog,
      ...feedbackAllowChecks,
      hasFeedbackServiceError,
    };
  }, [hasFeedbackServiceError, openFeedbackDialog, feedbackAllowChecks]);
  const feedbackDialog = useMemo(() => {
    if (!submitFeedback || !open) return null;
    if (isRatedFeedback(feedbackDialogType)) {
      if (!feedbackDialogRating) return null;
      switch (feedbackDialogType) {
        case FeedbackDialogType.RatedAssistantFeedback: {
          return (
            <ShareRatedFeedbackDialog
              rating={feedbackDialogRating}
              open={open}
              submitFeedback={submitFeedback.callback}
              onClose={onClose}
            />
          );
        }
        default: {
          return null;
        }
      }
    }
    switch (feedbackDialogType) {
      case FeedbackDialogType.GeneralDocsFeedback: {
        return (
          <ShareGeneralFeedbackDialog
            open={open}
            submitFeedback={submitFeedback.callback}
            onClose={onClose}
            isPublicDevForumFeedbackAllowed={feedbackAllowChecks.isPublicDevForumFeedbackAllowed}
          />
        );
      }
      default: {
        return null;
      }
    }
  }, [
    open,
    feedbackDialogType,
    feedbackDialogRating,
    feedbackAllowChecks.isPublicDevForumFeedbackAllowed,
    submitFeedback,
  ]);

  return (
    <ShareFeedbackContext.Provider value={contextValue}>
      <React.Fragment>
        {children}
        {feedbackDialog}
      </React.Fragment>
    </ShareFeedbackContext.Provider>
  );
};

export const useShareFeedbackProvider = () => {
  const context = useContext(ShareFeedbackContext);
  if (!context) {
    throw new Error(
      'useProvidedConversationReducer must be used within a ConversationReducerProvider',
    );
  }
  return context;
};
